// SimpleGameCard — live standings for a Simple side game.
//
// Simple = low gross wins each hole; no carry-over, no partnerships.
// Ties on a hole → no one wins. Unit_value pays per opponent per
// won hole (see docs/side-games.md § The Simple Game).
//
// Read-only display of get_simple_state:
//   • Standings tile per player: wins, running units (colored ±).
//   • Per-hole strip: winner_id → colored chip for that player.
//
// Component name is SimpleGameCard (not SimpleCard) to avoid clashing
// with the generic "simple" adjective in this codebase.

import { useCallback, useEffect, useState } from 'react'
import { db } from '../lib/db'

export default function SimpleGameCard({ game_id, autoRefreshMs = 0 }) {
  const [state, setState] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const { data } = await db('get_simple_state', { game_id })
      setState(data)
      setError(data?.error || null)
    } catch (e) {
      setError(e.message || 'Simple compute failed')
    }
    if (!silent) setLoading(false)
  }, [game_id])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!autoRefreshMs) return
    const t = setInterval(() => load({ silent: true }), autoRefreshMs)
    return () => clearInterval(t)
  }, [autoRefreshMs, load])

  if (loading && !state) return <p className="text-muted text-sm">Loading Simple…</p>
  if (error && !state) return <p style={{ color: 'var(--red)' }}>{error}</p>
  if (!state) return null

  const { participants = [], holes = [], totals = {} } = state
  const nameFor = (pid) => participants.find(p => p.player_id === pid)?.name || pid.slice(0, 6)

  // Rank by wins desc, then units desc.
  const ranked = [...participants]
    .map(p => ({ ...p, wins: totals[p.player_id]?.wins || 0, units: totals[p.player_id]?.units || 0 }))
    .sort((a, b) => (b.wins - a.wins) || (b.units - a.units))

  // Color map so per-hole chips are recognizable across the strip.
  const playerColors = colorMap(participants.map(p => p.player_id))

  return (
    <div>
      <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
        {ranked.map((p, i) => (
          <div key={p.player_id} style={{
            display: 'grid', gridTemplateColumns: 'auto 1fr auto auto', gap: 10,
            alignItems: 'center',
            padding: '8px 12px', borderRadius: 6,
            background: 'var(--green-dark)',
            border: '1px solid var(--green-mid)',
          }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
              {i + 1}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: playerColors.get(p.player_id),
                flexShrink: 0,
              }} />
              <span style={{ fontWeight: 600, fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nameFor(p.player_id)}
              </span>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-300)' }}>
              {p.wins} win{p.wins === 1 ? '' : 's'}
            </span>
            <span style={{
              fontFamily: 'var(--font-mono)', fontSize: '0.85rem', fontWeight: 700,
              color: p.units > 0 ? 'var(--blue-birdie)' : p.units < 0 ? 'var(--red)' : 'var(--gray-400)',
              minWidth: 60, textAlign: 'right',
            }}>
              {p.units > 0 ? '+' : ''}{p.units.toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      {holes.length > 0 && (
        <div>
          <p style={{ fontSize: '0.7rem', color: 'var(--gray-300)', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Hole-by-hole winners
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 3 }}>
            {holes.map(h => {
              const color = h.winner_id ? playerColors.get(h.winner_id) : 'transparent'
              const scores = Object.entries(h.scores)
                .map(([pid, s]) => `${nameFor(pid)}: ${s}`)
                .join(' · ')
              return (
                <div
                  key={h.hole}
                  title={`Hole ${h.hole}${h.par ? ` · par ${h.par}` : ''}\n${scores}${h.tied ? '\nTied — no winner' : ''}`}
                  style={{
                    padding: '4px 0', borderRadius: 4,
                    background: h.winner_id ? color : 'var(--green-deep)',
                    color: h.winner_id ? 'var(--green-deep)' : 'var(--gray-400)',
                    textAlign: 'center',
                    fontFamily: 'var(--font-mono)', fontSize: '0.68rem', fontWeight: 700,
                    border: '1px solid var(--green-mid)',
                  }}
                >
                  {h.hole}
                </div>
              )
            })}
          </div>
          <p style={{ fontSize: '0.65rem', color: 'var(--gray-500)', margin: '6px 0 0' }}>
            Colored chip = that player won the hole outright · dark = tied
          </p>
        </div>
      )}
    </div>
  )
}

// Palette that stays readable on the green background — kept small so
// 5+ player Simple games still look distinct enough.
const PALETTE = ['#D4B97A', '#7DD3A8', '#F88A92', '#8FCEE1', '#E8B36C']
function colorMap(ids) {
  const m = new Map()
  ids.forEach((id, i) => m.set(id, PALETTE[i % PALETTE.length]))
  return m
}
